import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Loader,
  Tab,
  TabHeaders,
  Tabs,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { PageLayout } from "@transfr-inc/dashboard-components/layouts";
import { getProductName } from "@transfr-inc/dashboard-components/utils";

import { RecordStatus } from "@components/custom/dropdowns/record-status-dropdown";
import { CourseTabHeader } from "@components/custom/tabs/course-tab-header";
import { ProductTabHeader } from "@components/custom/tabs/product-tab-header";
import { copyToClipboard } from "@lib/browser-utils";
import { useApiRequest } from "@lib/hooks";
import { classroomIdParser } from "@lib/utils/search-params-parsers";
import {
  useCurrentUser,
  useLearnerDashboardEnabled,
  useUserClassroomCourses,
  useRole,
} from "@queries";
import { useContainer } from "@src/contexts/container-context";

import { CourseTab } from "@views/my-work/components/course/course.tab";
import { MyWorkTab } from "@views/my-work/components/my-work.tab";
import { TabType } from "@views/my-work/models/tab-type";

import "./index.scss";
import { usePreviewClassroomModeEnabled } from "@hooks/queries";
import { useAppStore } from "@src/stores/zustand/app-store";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

export default () => {
  const urlPreviewId = useQueryState("preview")[0];

  const setGlobalPreviewId = useAppStore(
    (state) => state.setPreviewClassroomId
  );
  const storedPreviewId = useAppStore((state) => state.previewClassroomId);

  useEffect(() => {
    if (urlPreviewId) {
      setGlobalPreviewId(urlPreviewId);
    }
  }, [urlPreviewId, setGlobalPreviewId]);

  const previewClassroomId = urlPreviewId || storedPreviewId;

  const [selectedTab, setSelectedTab] = useQueryState(
    "classroomId",
    classroomIdParser
  );

  const currentUser = useCurrentUser();
  const role = useRole();
  const learnerDashboardEnabled = useLearnerDashboardEnabled();
  const { userId, organizationCode } = currentUser || {};

  const { organizationService, userService, classroomService } = useContainer();
  const isUrlPreviewEnabled = usePreviewClassroomModeEnabled();
  const isPreviewClassroomEnabled =
    !!previewClassroomId && (isUrlPreviewEnabled || !!storedPreviewId);

  const { data: userClassroomCourses, isLoading: userClassroomCoursesLoading } =
    useUserClassroomCourses(userId, {
      enabled: !!userId && !isPreviewClassroomEnabled,
    });

  const { response: previewClassroom, loading: previewLoading } = useApiRequest(
    () => classroomService.getClassroomDetails(previewClassroomId),
    isPreviewClassroomEnabled
  );

  const [isLoading, setIsLoading] = useState(true);
  const [trainee, setTrainee] = useState();

  const { loading: loadingTrainee } = useApiRequest(() =>
    userService
      .getUserDetails(userId, { status: RecordStatus.All })
      .then(setTrainee)
  );

  const { response: organization, sendRequest: getOrgDetails } = useApiRequest(
    () => organizationService.getOrganizationDetails(organizationCode),
    false
  );

  useEffect(() => {
    if (!loadingTrainee && trainee && !organization) {
      getOrgDetails();
    }
  }, [loadingTrainee, trainee, organization]);

  const classroomCourses = useMemo(() => {
    if (isPreviewClassroomEnabled) {
      if (!previewClassroom) return [];

      return [
        {
          ...previewClassroom,
          type:
            previewClassroom.courseId && learnerDashboardEnabled
              ? TabType.COURSE
              : TabType.CLASSROOM,
        },
      ];
    }

    if (!userClassroomCourses) return [];

    return userClassroomCourses.map((r) => ({
      ...r,
      type:
        r.courseId && learnerDashboardEnabled
          ? TabType.COURSE
          : TabType.CLASSROOM,
    }));
  }, [
    userClassroomCourses,
    learnerDashboardEnabled,
    isPreviewClassroomEnabled,
    previewClassroom,
  ]);

  useEffect(() => {
    const mainDataLoading = isPreviewClassroomEnabled
      ? previewLoading
      : userClassroomCoursesLoading;

    setIsLoading(mainDataLoading || loadingTrainee || !organization);
  }, [
    userClassroomCoursesLoading,
    loadingTrainee,
    organization,
    isPreviewClassroomEnabled,
    previewLoading,
  ]);

  useEffect(() => {
    if (!isLoading && classroomCourses?.length > 0) {
      const selectedTabName = getSelectedTab();
      const tabHeadersContainer = document.querySelector(".my-work-tabs");

      if (tabHeadersContainer && selectedTabName) {
        let selectedTabElement = tabHeadersContainer.querySelector(
          `[name="${selectedTabName}"]`
        );

        if (!selectedTabElement) {
          const allButtons = tabHeadersContainer.querySelectorAll("button");
          selectedTabElement = Array.from(allButtons).find(
            (button) => button.textContent?.trim() === selectedTabName
          );
        }

        if (selectedTabElement) {
          selectedTabElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }
    }
  }, [isLoading, classroomCourses, selectedTab]);

  const getSelectedTab = () => {
    if (selectedTab) {
      const foundTab = classroomCourses.find(
        (c) => c.classroomId === selectedTab
      );
      return foundTab?.classroomId;
    }

    return classroomCourses[0]?.classroomId;
  };

  const setTabState = useCallback(
    (tabName) => {
      setSelectedTab(tabName);
    },
    [setSelectedTab]
  );

  const renderTabs = useCallback(
    (classroomCourses, learnerDashboardEnabled, trainee, organization) => {
      return classroomCourses.map((item) => {
        if (item.type === TabType.CLASSROOM) {
          return (
            <Tab
              key={item.classroomId}
              name={item.classroomId}
              className={clsx(
                "classroom-tab",
                item?.products?.map(
                  (p) => `my-work-tab-${getProductName(p)?.toLowerCase()}`
                )
              )}
            >
              <h2
                className="classroom-code"
                onClick={() => copyToClipboard(item.code)}
              >
                Classroom Code
                <div className="code">
                  {item.code}
                  <TextTooltip
                    className="info"
                    text="Copied to clipboard"
                    trigger="click"
                  >
                    <FontAwesomeIcon icon="fa-regular fa-copy" />
                  </TextTooltip>
                </div>
              </h2>
              <MyWorkTab
                trainee={trainee}
                classroom={item}
                organization={organization}
                isPreview={isPreviewClassroomEnabled && !!previewClassroom?.id}
              />
            </Tab>
          );
        } else if (item.type === TabType.COURSE && learnerDashboardEnabled) {
          return (
            <Tab
              key={item.classroomId}
              name={item.classroomId}
              className={"classroom-tab"}
            >
              <CourseTab id={item.courseId} classroom={item} />
            </Tab>
          );
        }
        return null;
      });
    },
    [isPreviewClassroomEnabled, previewClassroom]
  );

  const renderTabHeaders = useCallback(
    (classroomCourses, learnerDashboardEnabled) => {
      return classroomCourses.map((item) => {
        if (item.type === TabType.CLASSROOM) {
          return (
            <ProductTabHeader
              key={item.classroomId}
              name={item.classroomId}
              productId={item.products?.[0]}
              text={item.name}
            />
          );
        } else if (item.type === TabType.COURSE && learnerDashboardEnabled) {
          return (
            <CourseTabHeader
              key={item.classroomId}
              name={item.classroomId}
              text={item.name}
            />
          );
        }
        return null;
      });
    },
    []
  );

  return isLoading ? (
    <Loader />
  ) : (
    <PageLayout className={"trainee-detail-page"}>
      <div className="trainee-detail-content">
        <div
          className={clsx(
            "trainee-classrooms-header",
            previewClassroomId && "hidden"
          )}
        >
          <h2>My Work</h2>
        </div>
        {classroomCourses?.length > 0 ? (
          <Tabs
            className="trainee-detail-tabs"
            selectedTabName={getSelectedTab()}
          >
            <TabHeaders
              className={clsx("my-work-tabs", previewClassroomId && "hidden")}
              onSelectedTabChange={setTabState}
            >
              {renderTabHeaders(classroomCourses, learnerDashboardEnabled)}
            </TabHeaders>
            {renderTabs(
              classroomCourses,
              learnerDashboardEnabled,
              trainee,
              organization
            )}
          </Tabs>
        ) : (
          <div className="no-classrooms-container">
            This {getLearnerTerm({ lowercase: true })} is not enrolled in a
            classroom.
          </div>
        )}
      </div>
    </PageLayout>
  );
};
